import { DOCUMENT_TYPES, type DocumentType } from '@paguz/contracts';
import { Button, Input, Label, inputBaseClass, cn } from '@paguz/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import {
  TERMS_VERSION,
  normalizePhone,
  startOtp,
  verifyOtp,
  type SignupPayload,
} from '../../api/auth.ts';
import { customerMessage } from '../../api/errors.ts';
import { queryKeys } from '../../api/query-client.ts';
import { InlineAlert } from '../../components/feedback.tsx';
import { PageHeader } from '../../components/layout.tsx';
import { OtpStep } from './otp-step.tsx';

interface FormState {
  fullName: string;
  documentType: DocumentType;
  documentNumber: string;
  phone: string;
  businessName: string;
  acceptTerms: boolean;
  acceptHabeasData: boolean;
}

type Errors = Partial<Record<keyof FormState, string>>;

export function validateSignup(form: FormState): Errors {
  const errors: Errors = {};
  if (form.fullName.trim().length < 3) errors.fullName = 'Escribe tu nombre y apellido.';
  if (!/^[0-9A-Z]{4,15}$/.test(form.documentNumber))
    errors.documentNumber = 'Escribe el número sin puntos ni espacios (4 a 15 caracteres).';
  if (normalizePhone(form.phone) === null)
    errors.phone = 'Escribe tu celular de 10 dígitos, por ejemplo 300 111 2233.';
  if (form.businessName.trim().length < 2) errors.businessName = '¿Cómo se llama tu negocio?';
  if (!form.acceptTerms) errors.acceptTerms = 'Debes aceptar los términos para continuar.';
  if (!form.acceptHabeasData)
    errors.acceptHabeasData = 'Necesitamos tu autorización para tratar tus datos.';
  return errors;
}

function Field({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error !== undefined && (
        <p id={`${id}-error`} className="text-sm font-medium text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

/** Registro de probador: nombre, documento, celular y negocio → OTP. */
export function SignupPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>({
    fullName: '',
    documentType: 'CC',
    documentNumber: '',
    phone: '',
    businessName: '',
    acceptTerms: false,
    acceptHabeasData: false,
  });
  const [errors, setErrors] = useState<Errors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState<{
    phone: string;
    challengeId: string;
    demoCode: string | undefined;
  } | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };
  const invalid = (key: keyof FormState) => ({
    'aria-invalid': errors[key] !== undefined || undefined,
    'aria-describedby': errors[key] !== undefined ? `${key}-error` : undefined,
  });

  async function requestCode(phone: string) {
    const result = await startOtp(phone, 'SIGNUP');
    setChallenge({ phone, challengeId: result.challengeId, demoCode: result.demoCode });
  }

  async function onSubmit() {
    const found = validateSignup(form);
    setErrors(found);
    const phone = normalizePhone(form.phone);
    if (Object.keys(found).length > 0 || phone === null) return;
    setBusy(true);
    setSubmitError(null);
    try {
      await requestCode(phone);
    } catch (err) {
      setSubmitError(customerMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (challenge) {
    const signupData: SignupPayload = {
      fullName: form.fullName.trim(),
      documentType: form.documentType,
      documentNumber: form.documentNumber,
      acceptTerms: true,
      acceptHabeasData: true,
      termsVersion: TERMS_VERSION,
      businessName: form.businessName.trim(),
    };
    return (
      <div className="flex flex-1 flex-col px-4 pt-6 pb-8">
        <PageHeader title="Confirma tu celular" />
        <OtpStep
          phone={challenge.phone}
          demoCode={challenge.demoCode}
          onChangePhone={() => {
            setChallenge(null);
          }}
          onResend={() => requestCode(challenge.phone)}
          onVerify={async (code) => {
            const customer = await verifyOtp({
              challengeId: challenge.challengeId,
              code,
              signupData,
            });
            queryClient.setQueryData(queryKeys.me, customer);
            await navigate({ to: '/', replace: true });
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col px-4 pt-6 pb-8">
      <PageHeader title="Crea tu cuenta" back="/ingresar" />
      <p className="mb-5 text-muted-foreground">
        Regístrate como probador. Solo te pedimos lo básico; no uses datos reales de clientes.
      </p>
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
      >
        <Field id="fullName" label="Nombre completo" error={errors.fullName}>
          <Input
            id="fullName"
            autoComplete="name"
            value={form.fullName}
            onChange={(e) => {
              set('fullName', e.target.value);
            }}
            {...invalid('fullName')}
          />
        </Field>
        <Field id="documentType" label="Tipo de documento" error={undefined}>
          <select
            id="documentType"
            className={cn(inputBaseClass, 'appearance-none')}
            value={form.documentType}
            onChange={(e) => {
              set('documentType', e.target.value as DocumentType);
            }}
          >
            {DOCUMENT_TYPES.entries
              .filter((entry) => entry.code !== 'NIT' && entry.code !== 'TI')
              .map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {entry.label}
                </option>
              ))}
          </select>
        </Field>
        <Field id="documentNumber" label="Número de documento" error={errors.documentNumber}>
          <Input
            id="documentNumber"
            inputMode={form.documentType === 'PA' ? 'text' : 'numeric'}
            value={form.documentNumber}
            onChange={(e) => {
              set('documentNumber', e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, ''));
            }}
            {...invalid('documentNumber')}
          />
        </Field>
        <Field id="phone" label="Celular" error={errors.phone}>
          <Input
            id="phone"
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            placeholder="300 111 2233"
            value={form.phone}
            onChange={(e) => {
              set('phone', e.target.value);
            }}
            {...invalid('phone')}
          />
        </Field>
        <Field id="businessName" label="Nombre de tu negocio" error={errors.businessName}>
          <Input
            id="businessName"
            autoComplete="organization"
            placeholder="Tienda Rosita"
            value={form.businessName}
            onChange={(e) => {
              set('businessName', e.target.value);
            }}
            {...invalid('businessName')}
          />
        </Field>
        <Checkbox
          id="acceptTerms"
          checked={form.acceptTerms}
          onChange={(value) => {
            set('acceptTerms', value);
          }}
          error={errors.acceptTerms}
        >
          Acepto los términos y condiciones del entorno de demostración.
        </Checkbox>
        <Checkbox
          id="acceptHabeasData"
          checked={form.acceptHabeasData}
          onChange={(value) => {
            set('acceptHabeasData', value);
          }}
          error={errors.acceptHabeasData}
        >
          Autorizo el tratamiento de mis datos personales (habeas data).
        </Checkbox>
        {submitError !== null && (
          <InlineAlert tone="danger">
            {submitError}{' '}
            <Link to="/ingresar" className="font-semibold underline">
              Ir a ingresar
            </Link>
          </InlineAlert>
        )}
        <Button type="submit" size="lg" loading={busy} className="mt-2">
          Recibir código
        </Button>
      </form>
    </div>
  );
}

function Checkbox({
  id,
  checked,
  onChange,
  error,
  children,
}: {
  id: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  error: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="flex min-h-11 cursor-pointer items-start gap-3 text-base">
        <input
          id={id}
          type="checkbox"
          className="mt-1 size-5 shrink-0 accent-primary"
          checked={checked}
          onChange={(e) => {
            onChange(e.target.checked);
          }}
          aria-invalid={error !== undefined || undefined}
          aria-describedby={error !== undefined ? `${id}-error` : undefined}
        />
        <span>{children}</span>
      </label>
      {error !== undefined && (
        <p id={`${id}-error`} className="pl-8 text-sm font-medium text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
